import { useMemo, useState, useEffect } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { assetUrl } from 'utils/url'
import { GPK_PEGAWAI_TUGAS_POKOK } from 'app/KinerjaGpkPage/constants/contents'
import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useUpsertPerencanaan } from 'app/KinerjaGpkPage/hooks/use-upsert-perencanaan'
import { IndikatorPerencanaanSelect } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { useGpkTracker } from 'app/KinerjaGpkPage/hooks/use-gpk-tracker'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { OptionItem } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import type { UpsertSKPPayload } from 'types/kinerja'

type PerencanaanPegawaiTugasPokokProps = {
  onNext: () => void
}

export function PerencanaanPegawaiTugasPokok({
  onNext,
}: PerencanaanPegawaiTugasPokokProps) {
  const router = useRouter()
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()
  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'GPK',
      }
    )
  // Safely get the first item of the `tugasTambahan`
  // since it always have only 1 item
  const currentTugasTambahan = perencanaanData?.data?.tugasTambahan?.[0]

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedTugasTambahan, setSelectedTugasTambahan] =
    useState<OptionItem>(undefined)

  const isDirty = useMemo(() => {
    return selectedTugasTambahan?.value !== currentTugasTambahan?.id
  }, [currentTugasTambahan, selectedTugasTambahan])

  // Sync with perencanaan data
  useEffect(() => {
    if (currentTugasTambahan) {
      setSelectedTugasTambahan({
        value: currentTugasTambahan?.id,
        label: '',
      })
    }
  }, [currentTugasTambahan])

  // -----------------------------------------
  // Get Tugas Tambahan content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
    isSuccess: isSuccessContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'GPK',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const tugasTambahanOptions: Array<OptionItem> = useMemo(() => {
    return (
      dataContentKinerjaUtama?.data?.tugasTambahan?.map((item) => ({
        value: item.id,
        label: item.rencanaHasilKerja,
      })) || []
    )
  }, [dataContentKinerjaUtama])

  const tugasPokokIds = useMemo(() => {
    return (
      dataContentKinerjaUtama?.data?.kinerjaUtama?.map((item) => item.id) || []
    )
  }, [dataContentKinerjaUtama])

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  // Track visit event
  useEffect(() => {
    if (isSuccessContentKinerjaUtama) {
      trackEvent({
        activityType: 'view_pelaksanaan_tugas_pokok',
        screenName: 'perencanaan_pegawai',
        attributes: {
          year: String(periodYear),
        },
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessContentKinerjaUtama])

  // Track save action
  const handleTrackSaveAction = () => {
    trackEvent({
      activityType: 'save_pelaksanaan_tugas_pokok',
      screenName: 'perencanaan_pegawai',
      attributes: {
        year: String(periodYear),
      },
    })
  }

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      kinerjaUtama: tugasPokokIds,
      tugasTambahan: selectedTugasTambahan
        ? [{ id: Number(selectedTugasTambahan.value), kuantitas: 1 }]
        : [{ id: undefined, kuantitas: 0 }],
      tipe: 'GPK',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perencanaanData, selectedTugasTambahan?.value, tugasPokokIds]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      shouldShowSuccessMessage: true,
      onSuccess: () => {
        router.push(`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}`)

        // Track save action if the form is dirty
        if (isDirty) {
          handleTrackSaveAction()
        }
      },
    })

  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      onSuccess: () => {
        onNext()
        handleTrackSaveAction()
      },
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-center gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={64}
            height={64}
          />
          <Text className="max-w-4xl">
            Berikut tugas keseharian dan tugas tambahan Anda sebagai Guru
            Pendidikan Khusus di satuan pendidikan. Anda tidak perlu unggah
            Bukti Dukung.
          </Text>
        </div>

        <div className="space-y-6">
          {GPK_PEGAWAI_TUGAS_POKOK.map((item, index) => (
            <IndikatorPerencanaanPanel
              key={index}
              orderNumber={index + 1}
              title={item}
            />
          ))}

          <IndikatorPerencanaanSelect
            isLoading={isLoading}
            allowReset={true}
            contents={{
              label: 'Opsional',
              title:
                'Terlaksananya penugasan tambahan dalam mendukung layanan pembelajaran bagi murid penyandang disabilitas GPK',
              orderNumber: GPK_PEGAWAI_TUGAS_POKOK.length + 1,
            }}
            options={tugasTambahanOptions}
            selected={selectedTugasTambahan}
            onChange={setSelectedTugasTambahan}
          />
        </div>
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          disabled={isLoading || !isDirty}
          onClick={handleSaveToDraft}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={isLoading}
          onClick={handleNext}
        >
          Ke Praktik Kinerja
        </Button>
      </div>
    </>
  )
}
